import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import React, { useCallback, useEffect, useState } from "react";
import TextField from "@mui/material/TextField";
import Checkbox from "@mui/material/Checkbox";
import ListItemText from "@mui/material/ListItemText";
import "./styles.scss";
import FormControlLabel from "@mui/material/FormControlLabel";
import ImageField from "./ImageField";
import _debounce from "lodash/debounce";
import FileField from "./FileField";
import Autocomplete from "@mui/material/Autocomplete";
import DurationField from "./DurationField";

function FormField({
  field,
  isEmpty = false,
  onHandleChange = () => {},
  variant = "filled",
  placeholder = "",
  newValue = null,
  dependenceValueIsSet = "",
  dependenceTitle = "",
}) {
  const [value, setValue] = useState(field.multiple ? [] : "");

  useEffect(() => {
    let fieldValue = field.value;

    if (field.dependence && !dependenceValueIsSet) {
      fieldValue = field.multiple ? [] : "";
    }

    if (!["image", "big-image"].includes(field.type)) {
      if (
        ["text", "number", "string"].includes(field.type) &&
        !fieldValue &&
        !field.multiple
      ) {
        initValue("");
      } else {
        let val = fieldValue ?? "";
        if (field.multiple) {
          val = [...(fieldValue ?? [])];
        }
        initValue(val);
      }
    }
  }, [dependenceValueIsSet]);

  useEffect(() => {
    if (newValue !== null && value !== newValue) {
      initValue(newValue);
    }
  }, [newValue]);

  useEffect(() => {
    // console.log(value, field.id, field.value, "field.value");
  }, [value]);

  const initValue = (val) => {
    if (field.type === "boolean" && !val) {
      val = false;
    }

    setValue(val);
    onHandleChange({ value: val, code: field.id });
  };

  const handleChange = (value) => {
    setValue(value);
    debounceFn(value);
  };
  const handleFileChange = (value) => {
    setValue(value);
    onHandleChange({ value: value, code: field.id });
  };

  const change = (value) => {
    onHandleChange({ value: value, code: field.id });
  };

  const debounceFn = useCallback(_debounce(change, 500), []);

  const getSelectedText = (selected) => {
    let arr = selected.map((item) => {
      let option = field.items.find((el) => el.value === item);
      return option?.text;
    });
    return <span dangerouslySetInnerHTML={{ __html: arr.join(", ") }} />;
  };

  const isShown = () => {
    return (field.dependence && dependenceValueIsSet) || !field.dependence;
  };

  const multiChange = (text, index) => {
    let val = [...value];
    val[index] = text;
    handleChange(val);
  };

  return (
    <>
      {isShown() && (
        <div
          className={
            "detail-form-field " +
            field.id +
            " " +
            field.type +
            " " +
            (isEmpty ? "empty" : "") +
            (field.disabled ? " disabled" : "")
          }
        >
          {field.type !== "boolean" && (
            <label className="detail-form-field__label">
              {dependenceTitle || field.title} {field.required && <sup>*</sup>}
            </label>
          )}
          {field.type === "autocomplete" && !field.multiple && (
            <Autocomplete
              id={field.id}
              options={field.items}
              onChange={(e, newValue) => handleChange(newValue?.value)}
              defaultValue={
                field.items.find((el) => el.value === field.value) ?? ""
              }
              getOptionLabel={(option) => option?.text ?? ""}
              sx={{
                ".MuiInputBase-root": {
                  paddingTop: 0,
                },
              }}
              renderInput={(params) => (
                <TextField {...params} size="medium" variant="filled" />
              )}
            />
          )}
          {field.type === "list" && !field.multiple && (
            <Select
              id={field.id}
              name={field.id}
              required={field.required}
              value={value}
              size="medium"
              variant="filled"
              onChange={(e) => handleChange(e.target.value)}
              defaultValue={field.value ?? ""}
            >
              {field.items.map((item) => (
                <MenuItem key={item.value} value={item.value}>
                  {item.text}
                </MenuItem>
              ))}
            </Select>
          )}
          {field.type === "list" && field.multiple && (
            <Select
              id={field.id}
              name={field.id}
              required={field.required}
              value={value || []}
              multiple={field.multiple}
              variant="filled"
              size="medium"
              onChange={(e) => handleChange(e.target.value)}
              renderValue={(selected) => getSelectedText(selected)}
            >
              {field.items.map((item) => (
                <MenuItem key={item.value} value={item.value}>
                  <Checkbox checked={value.indexOf(item.value) > -1} />
                  <ListItemText
                    primary={
                      <span dangerouslySetInnerHTML={{ __html: item?.text }} />
                    }
                  />
                </MenuItem>
              ))}
            </Select>
          )}
          {["textarea"].includes(field.type) && !field.multiple && (
            <TextField
              name={field.id}
              value={value}
              type={field.type}
              required={field.required}
              size="medium"
              variant={variant}
              placeholder={placeholder}
              multiline
              rows={5}
              onChange={(e) => handleChange(e.target.value)}
              autoComplete="off"
            />
          )}
          {["text", "string", "number"].includes(field.type) &&
            !field.multiple && (
              <TextField
                name={field.id}
                value={value}
                type={field.type}
                required={field.required}
                size="medium"
                variant={variant}
                placeholder={placeholder}
                onChange={(e) => handleChange(e.target.value)}
                autoComplete="off"
              />
            )}
          {["text", "string", "number"].includes(field.type) &&
            field.multiple && (
              <>
                {value.length > 0 &&
                  value.map((val, i) => (
                    <TextField
                      key={"key-" + i}
                      name={field.multiple ? `${field.id}[${i}]` : field.id}
                      value={val}
                      type={field.type}
                      required={field.required}
                      size="medium"
                      variant={variant}
                      placeholder={placeholder}
                      onChange={(e) => multiChange(e.target.value, i)}
                      autoComplete="off"
                    />
                  ))}
                {value.length === 0 && (
                  <TextField
                    name={field.multiple ? `${field.id}[]` : field.id}
                    value={value}
                    type={field.type}
                    required={field.required}
                    size="medium"
                    variant={variant}
                    placeholder={placeholder}
                    onChange={(e) => multiChange(e.target.value, 0)}
                    autoComplete="off"
                  />
                )}

                {/*<span className="add-more" onClick={()=>addMore()}><AddCircleOutlineRoundedIcon /></span>*/}
              </>
            )}
          {field.type === "boolean" && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!value}
                  onChange={(e) => {
                    handleChange(e.target.checked);
                  }}
                />
              }
              label={field.title}
            />
          )}
          {field.type === "file" && (
            <FileField field={field} onHandleChange={handleFileChange} />
          )}
          {field.type === "image" && (
            <ImageField field={field} onHandleChange={handleFileChange} />
          )}

          {field.type === "duration" && (
            <DurationField field={field} onHandleChange={handleChange} />
          )}

          {field.type === "datetime" && (
            <TextField
              id={field.id}
              name={field.id}
              type="datetime-local"
              value={value}
              // defaultValue="2017-05-24T10:30"
              size="medium"
              variant={variant}
              sx={{ width: 250 }}
              InputLabelProps={{
                shrink: true,
              }}
              onChange={(e) => handleChange(e.target.value)}
              autoComplete="off"
            />
          )}

          {field.type === "date" && (
            <TextField
              id={field.id}
              name={field.id}
              type="date"
              value={value}
              // defaultValue="2017-05-24T10:30"
              size="medium"
              variant={variant}
              sx={{ width: 250 }}
              InputLabelProps={{
                shrink: true,
              }}
              onChange={(e) => handleChange(e.target.value)}
              autoComplete="off"
            />
          )}

          {field.subtext && (
            <div
              dangerouslySetInnerHTML={{ __html: field.subtext }}
              className="field-subtext"
            />
          )}
        </div>
      )}
    </>
  );
}

export default FormField;
